var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from "react";
import { SpinnerBase } from "../animations/spinner";
import styled, { css } from "../styled";
import pxToRem from "../utils/px-to-rem";
import { createPolymorphicComponent } from "../utils/types/polymorphic-component";
import BaseButton from "./button";
import { DEFAULT_SIZE, DEFAULT_VARIANT } from "./constants";
export const iconSizeMap = {
    large: pxToRem(20),
    medium: pxToRem(18),
    small: pxToRem(18),
};
const sizeStyleSelector = ({ size = DEFAULT_SIZE, variant = DEFAULT_VARIANT, theme, }) => {
    const { space, radii } = theme._ui;
    const minSizeStyles = {
        large: css `
      min-height: ${pxToRem(48)};
      min-width: ${pxToRem(48)};
    `,
        medium: css `
      min-height: ${pxToRem(40)};
      min-width: ${pxToRem(40)};
    `,
        small: css `
      min-height: ${pxToRem(32)};
      min-width: ${pxToRem(32)};
    `,
    };
    const styles = {
        large: css `
      border-radius: ${radii.medium};
      padding: ${space.s3} ${space.s4};
      gap: ${space.s2};
    `,
        medium: css `
      border-radius: ${radii.small};
      padding: ${space.s2} ${space.s3};
      gap: ${space.s1};
    `,
        small: css `
      border-radius: ${radii.small};
      padding: ${space.s1} ${space.s3};
      gap: ${space.s1};
    `,
    };
    return css `
    ${styles[size]}
    ${variant !== "inherit" && minSizeStyles[size]}
  `;
};
const stateStyleSelector = ({ variant = DEFAULT_VARIANT, theme, }) => {
    var _a, _b, _c, _d, _e, _f;
    const { colors } = theme._ui;
    const DEFAULT_BORDER_STYLE = "border-color: transparent";
    const borderStyle = {
        primary: DEFAULT_BORDER_STYLE,
        secondary: "",
        tertiary: DEFAULT_BORDER_STYLE,
        success: DEFAULT_BORDER_STYLE,
        error: DEFAULT_BORDER_STYLE,
        inherit: "",
    };
    const extendedMessageStates = {
        primary: {
            hover: colors.accent.bg.hover,
            pressed: colors.accent.bg.pressed,
        },
        secondary: {
            hover: colors.accent.bg.weaker,
            pressed: colors.accent.bg.weakerPressed,
        },
        tertiary: {
            hover: colors.accent.bg.weaker,
            pressed: colors.accent.bg.weakerPressed,
        },
        success: {
            hover: colors.positive.bg.hover,
            pressed: colors.positive.bg.pressed,
        },
        error: {
            hover: colors.negative.bg.hover,
            pressed: colors.negative.bg.pressed,
        },
        inherit: { hover: "inherit", pressed: "inherit", focused: "inherit" },
    };
    const outlineBorderColor = {
        primary: (_a = extendedMessageStates[variant]) === null || _a === void 0 ? void 0 : _a.hover,
        secondary: colors.surface.border.default,
        tertiary: colors.surface.border.default,
        success: (_b = extendedMessageStates[variant]) === null || _b === void 0 ? void 0 : _b.hover,
        error: (_c = extendedMessageStates[variant]) === null || _c === void 0 ? void 0 : _c.hover,
        inherit: "transparent",
    };
    const buttonStateStyles = `
      :not([disabled]) {
        &:hover {
          background: ${(_d = extendedMessageStates[variant]) === null || _d === void 0 ? void 0 : _d.hover};
          ${borderStyle[variant]};
        }

        &:focus-visible {
          background: ${(_e = extendedMessageStates[variant]) === null || _e === void 0 ? void 0 : _e.hover};
          outline: ${pxToRem(2)} solid ${colors.focus.default};
          outline-offset: ${pxToRem(2)};
          border: 1px solid ${outlineBorderColor[variant]};
        }

        &:active {
          background: ${(_f = extendedMessageStates[variant]) === null || _f === void 0 ? void 0 : _f.pressed};
          ${borderStyle[variant]};
        }
      }`;
    return buttonStateStyles;
};
const variantStyleSelector = ({ variant = DEFAULT_VARIANT, theme, disabled, isLoading, }) => {
    const { colors } = theme._ui;
    const styles = {
        primary: `
      color: ${colors.white.default};
      background: ${colors.accent.bg.default};
      border-color: ${colors.accent.bg.default};
    `,
        secondary: `
      color: ${colors.accent.text.default};
      background: ${colors.surface.bg.default};
      border-color: ${colors.surface.border.default};
    `,
        tertiary: `
      color: ${colors.accent.text.default};
      background: transparent;
      border-color: transparent;
    `,
        success: `
      color: ${colors.white.default};
      background: ${colors.positive.bg.default};
      border-color: ${colors.positive.bg.default};
    `,
        error: `
      color: ${colors.white.default};
      background: ${colors.negative.bg.default};
      border-color: ${colors.negative.bg.default};
    `,
        inherit: `
      color: inherit;
      background: inherit;
      border-color: transparent;
    `,
    };
    const commonDisabledStyle = css `
    color: ${colors.surface.text.disabled};
    background: ${colors.surface.bg.disabled};
    border-color: transparent;
  `;
    const disabledStyles = {
        primary: commonDisabledStyle,
        secondary: commonDisabledStyle,
        tertiary: css `
      ${commonDisabledStyle};
      background: transparent;
    `,
        inherit: css `
      ${commonDisabledStyle};
      background: inherit;
    `,
        success: commonDisabledStyle,
        error: commonDisabledStyle,
    };
    const commonLoadingStyle = css `
    color: ${colors.accent.bg.default};
    background: ${colors.surface.bg.disabled};
    border-color: transparent;
  `;
    const loadingStyles = {
        primary: commonLoadingStyle,
        secondary: commonLoadingStyle,
        tertiary: css `
      ${commonLoadingStyle};
      background: transparent;
    `,
        success: commonLoadingStyle,
        error: css `
      ${commonLoadingStyle};
      color: ${colors.negative.bg.default};
    `,
        inherit: css `
      ${commonLoadingStyle};
      background: inherit;
    `,
    };
    if (isLoading) {
        return loadingStyles[variant];
    }
    if (disabled) {
        return disabledStyles[variant];
    }
    return styles[variant];
};
export const buttonStyles = css `
  margin: 0;
  border: 1px solid;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: ${({ theme }) => theme._ui.font.size.label.medium};
  line-height: 22px;
  transition: background-color 200ms ease 0s;
  user-select: none;

  :not([disabled]):hover {
    cursor: pointer;
  }

  :disabled:hover {
    cursor: not-allowed;
  }

  ${sizeStyleSelector}
  ${variantStyleSelector}
  ${stateStyleSelector}
`;
export const StyledButton = styled(BaseButton) `
  ${buttonStyles}
`;
// We need to do this as spinner size and text are different
// We need to make the button height the same, when it has text or spinner
// At the moment, spinner only has fixed size for all button sizes.
const SpinnerBaseStyled = styled(SpinnerBase) `
  margin: ${pxToRem(3)} auto;
`;
export const Button = forwardRef((_a, ref) => {
    var { children, isLoading, disabled, size = DEFAULT_SIZE, startIcon: startIconProp, endIcon: endIconProp } = _a, props = __rest(_a, ["children", "isLoading", "disabled", "size", "startIcon", "endIcon"]);
    const startIcon = startIconProp &&
        React.cloneElement(startIconProp, {
            size: iconSizeMap[size],
        });
    const endIcon = endIconProp &&
        React.cloneElement(endIconProp, {
            size: iconSizeMap[size],
        });
    return (React.createElement(StyledButton, Object.assign({ disabled: disabled || isLoading, isLoading: isLoading, ariaLabel: isLoading ? "loading" : undefined, size: size }, props, { ref: ref }), isLoading ? (React.createElement(SpinnerBaseStyled, { "data-anchor": "button-spinner" })) : (React.createElement(React.Fragment, null,
        startIcon,
        children,
        endIcon))));
});
export default createPolymorphicComponent(Button);
